/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { TaskTypes } from '@userzoomplatform/pe-study-tasks/dist/types';

import QuestionnaireTask from './tasks/questionnaire-task';
import PrototypeTask from './tasks/prototype-task';
import TaskLoader from '../../components/task-loader';
import { TASKS_FIELDS } from './fragments';
import CardSortTask from './tasks/card-sort-task';

// TODO: remove "any" ignore when we decide how to manage complex types such as Task
/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable no-underscore-dangle, react/jsx-props-no-spreading */

const customizationQuery = gql`
	query customization {
		customization {
			language
			name
			systemTexts {
				mandatoryQuestion {
					ratingScale
					rankOrder
					choiceSingle
					choiceMatrixOrMultiple
					multipleMinimum
					multipleMaximum
					multipleExact
					openEndedSingle
					openEndedMultiple
					clickTest
					timeout
					other
					pageError
				}
				buttons {
					next
					startTask
					exitLink
					skip
				}
				miscellaneous {
					choiceDropdownDefault
					rankOrderInstructions
					clickTestClicksLabel
					screenshotQuestionsHelp
					treeTestSelectItemButton
					treeTestSelectedItemButton
					multimediaQuestionTab
				}
				cardSort {
					clickToName
					categories
					itemsLeft
					instructions
					continueButton
					confirmationMessage
					yesContinueButton
					cancelButton
					continueDragMessage
					continueDrag
					reviewInstructions
					addCategory
					itemInfo
					clickForItemInfo
					categoryName
					categoryDescription
					reviewItems
					askCategoryName
					modifyCategoryName
					askCategoryDescription
				}
			}
		}
	}
`;

const taskQuery = gql`
	${TASKS_FIELDS}
	query {
		task {
			...TasksFields
		}
	}
`;

const Task = (): ReactElement => {
	const { error: errorCustomization, loading: loadingCustomization, data: dataCustomization } = useQuery<any>(customizationQuery);

	const { error, loading } = useQuery<any>(taskQuery, {
		fetchPolicy: 'no-cache',
		onCompleted: (response: any) => {
			setCurrentTask(response.task);
		},
	});

	const [currentTask, setCurrentTask] = useState<any>();
	useEffect(() => {
		if (currentTask) {
			if (currentTask.__typename === TaskTypes.Finished) {
				window.location.href = currentTask.url;
			}
		}
	}, [currentTask]);

	if (loading || loadingCustomization) {
		return (
			<div className="task task--loading">
				<TaskLoader />
			</div>
		);
	}

	if (error || errorCustomization) {
		// TODO: handle error
		// eslint-disable-next-line no-console
		console.error(error);
		return <div className="task task--error" />;
	}

	if (currentTask) {
		const taskProps = {
			data: currentTask,
			setTask: setCurrentTask,
			systemTexts: dataCustomization.customization.systemTexts,
		};

		if (currentTask.__typename === TaskTypes.Questionnaire) {
			return <QuestionnaireTask {...taskProps} studyName={dataCustomization.customization.name} />;
		}

		if (currentTask.__typename === TaskTypes.Prototype) {
			return <PrototypeTask {...taskProps} />;
		}

		if (currentTask.__typename === TaskTypes.CardSort) {
			return <CardSortTask {...taskProps} />;
		}

		if (currentTask.__typename === TaskTypes.Finished) {
			return <div className="task task--finished" />;
		}

		return (
			<p>
				Unimplemented task:&nbsp;
				{currentTask.__typename}
			</p>
		);
	}

	return <div className="task--empty" />;
};

export default Task;
